/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { RouterProvider } from 'react-router-dom';
import { Suspense, useEffect } from 'react';

import { Spin } from '@coze-arch/coze-design';

import { router } from './routes';

export function App() {
  useEffect(() => {
    // Replace "Coze" with "Kisra" in document title
    const replaceCozeInTitle = () => {
      const currentTitle = document.title;

      // If title is exactly "Coze", replace with full brand name
      if (currentTitle.trim() === 'Coze') {
        document.title = 'KISRA AI';
        return;
      }

      // Otherwise, replace "Coze" with "Kisra" in the title
      if (currentTitle.includes('Coze')) {
        document.title = currentTitle.replace(/Coze/g, 'Kisra');
      }
    };

    // Run initial replacement
    replaceCozeInTitle();

    // Create a MutationObserver to watch for title changes
    const observer = new MutationObserver(() => {
      replaceCozeInTitle();
    });

    // Observe changes to the title element
    const titleElement = document.querySelector('title');
    if (titleElement) {
      observer.observe(titleElement, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Suspense
      fallback={
        <div className="w-full h-full flex items-center justify-center">
          <Spin spinning style={{ height: '100%', width: '100%' }} />
        </div>
      }
    >
      <RouterProvider router={router} fallbackElement={<div>loading...</div>} />
    </Suspense>
  );
}
